import React from "react";
import { motion } from "framer-motion";
import { CheckSquare, UserCheck, ClipboardList, Share2, Wallet } from "lucide-react";

const STEPS = [
  {
    icon: CheckSquare,
    title: "1. Check Eligibility",
    desc: "Paste your Reddit username or link to instantly verify your Karma (300+ required).",
    glow: "from-indigo-500/10 to-indigo-600/5",
    iconColor: "text-[#3F51B5]",
    iconBg: "bg-indigo-50 border-indigo-100",
  },
  {
    icon: UserCheck,
    title: "2. Register Profile",
    desc: "Fill out a quick form detailing your location, Telegram handle, and posting niches.",
    glow: "from-indigo-500/10 to-indigo-600/5",
    iconColor: "text-[#3F51B5]",
    iconBg: "bg-indigo-50 border-indigo-100",
  },
  {
    icon: ClipboardList,
    title: "3. Get Prompts",
    desc: "Receive daily client posting prompts matched directly to your Reddit sub-communities.",
    glow: "from-indigo-500/10 to-indigo-600/5",
    iconColor: "text-[#3F51B5]",
    iconBg: "bg-indigo-50 border-indigo-100",
  },
  {
    icon: Share2,
    title: "4. Share & Post",
    desc: "Publish the client link naturally in the college, tech, or lifestyle subreddits you browse.",
    glow: "from-indigo-500/10 to-indigo-600/5",
    iconColor: "text-[#3F51B5]",
    iconBg: "bg-indigo-50 border-indigo-100",
  },
  {
    icon: Wallet,
    title: "5. Earn ₹100 / Post",
    desc: "Submit your post URL for verification and get ₹100 credited instantly to your UPI.",
    glow: "from-amber-400/20 to-yellow-400/10",
    iconColor: "text-black",
    iconBg: "bg-[#FFC107] border-amber-300",
  },
];

export default function HowItWorksSection() {
  return (
    <section id="how-it-works" className="py-16 px-4 max-w-7xl mx-auto relative overflow-hidden scroll-mt-20">
      {/* Title */}
      <div className="text-center mb-16 relative">
        <h2 className="text-4xl sm:text-5xl font-black text-[#3F51B5] tracking-tight mb-4">
          How the Creator Program Works
        </h2>
        <p className="text-slate-600 text-md sm:text-lg font-semibold max-w-xl mx-auto">
          Five simple steps to unlock payouts for posting in your favorite Reddit communities.
        </p>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 relative">
        {STEPS.map((step, idx) => {
          const Icon = step.icon;
          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
              className="bg-white border-2 border-slate-200/80 rounded-3xl p-6 flex flex-col justify-between relative group hover:border-[#3F51B5] hover:shadow-xl hover:shadow-indigo-100/30 transition-all duration-300"
            >
              <div
                className={`absolute inset-0 bg-gradient-to-br ${step.glow} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl pointer-events-none`}
              />

              <div className="relative z-10">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-6 shadow-sm border ${step.iconBg}`}>
                  <Icon className={`w-6 h-6 ${step.iconColor}`} />
                </div>

                <h3 className="text-lg font-black text-slate-900 mb-2 tracking-wide">{step.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed font-semibold">{step.desc}</p>
              </div>

              {/* Step connector lines for desktop */}
              {idx < STEPS.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-[2px] bg-slate-200 group-hover:bg-[#3F51B5] transition-colors z-20" />
              )}
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
